"use client";
import { SchoolData } from "@nimble/candidate-api/types";
import * as React from "react";
import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

import { AcceptMatchFormValues } from "@/app/(discover)/candidate/invites/components/accept-match-modal/accept-match-form-schema";
import { PrincipalCardPreset } from "@/app/(discover)/candidate/invites/components/principal-card";
import ButtonLoader from "@/components/shared/button-loader";
import FormRootError from "@/components/shared/form/form-root-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

export function AcceptMatchModalView({
  formEl,
  onClose,
  schoolData,
}: {
  formEl: ReactNode;
  onClose: () => void;
  schoolData: SchoolData;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={true}
        onClick={(e) => e.stopPropagation()}
        className="min-w-0 overflow-x-hidden gap-0"
      >
        <DialogHeader className="pb-4 border-b border-border">
          <DialogTitle className="text-sm font-bold text-foreground">
            Express interest
          </DialogTitle>
          <DialogDescription className="text-xs text-ink-subtle mt-1">
            Send a message to the principal at this school
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 border-b border-line-faint">
          <PrincipalCardPreset schoolData={schoolData} />
        </div>

        <div className="pt-4">{formEl}</div>
      </DialogContent>
    </Dialog>
  );
}

export function AcceptMatchFormView({
  form,
  onCancel,
  onSubmit,
}: {
  form: UseFormReturn<AcceptMatchFormValues>;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex min-w-0 flex-col gap-4">
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Optional message — we recommend suggesting times to connect."
                  className="text-sm placeholder:text-xs min-h-[80px] shadow-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="bg-surface-subtle rounded-md px-3 py-3">
          <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2">
            What happens next
          </p>
          <ol className="text-xs text-ink-subtle space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-ink-faint font-medium">1.</span>
              <span>
                We&apos;ll reach out to the principal with your message and
                profile
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-ink-faint font-medium">2.</span>
              <span>Once they respond, you&apos;ll get a notification</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-ink-faint font-medium">3.</span>
              <span>Check your Connections tab and email for their reply</span>
            </li>
          </ol>
        </div>

        <FormRootError />

        <div className="flex flex-row gap-2 pt-2 border-t border-border">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            disabled={form.formState.isSubmitting}
            className="flex-1 shadow-none"
          >
            Cancel
          </Button>
          <ButtonLoader
            type="submit"
            disabled={form.formState.isSubmitting}
            loading={form.formState.isSubmitting}
            className="flex-1 bg-surface-inverted hover:bg-surface-inverted/90 shadow-none"
          >
            Send message
          </ButtonLoader>
        </div>
      </form>
    </Form>
  );
}
